/**
 * PAYMENT SURFACE BRANDING — SINGLE SOURCE OF TRUTH
 *
 * Customer-facing brand:  GIGGONE
 * Legal entity:           Sky Is The Limit Shop LLC
 *
 * Stripe shows "Sky Is The Limit Shop" on receipts today because that is the
 * account's *public business name*, not because of anything in this codebase.
 * Almost every customer-facing surface CAN read GIGGONE; only a short list is
 * genuinely tied to the registered entity.
 *
 * The rule that keeps this both on-brand and compliant: show the trade name
 * prominently, disclose the legal entity in the same view.
 */

export const CUSTOMER_BRAND = 'GIGGONE';
export const LEGAL_ENTITY = 'Sky Is The Limit Shop LLC';

/** Use on payment pages, receipts, confirmation emails, portal footers. */
export const BRAND_ATTRIBUTION = `${CUSTOMER_BRAND} — a service of ${LEGAL_ENTITY}`;
export const BRAND_ATTRIBUTION_SHORT = `Powered by ${CUSTOMER_BRAND} · Operated by ${LEGAL_ENTITY}`;

/**
 * Card statement descriptor. Max 22 characters, letters/numbers/spaces only,
 * must be recognisable to the cardholder. "GIGGONE" alone is allowed because
 * a trade name of the registered entity is acceptable to the card networks —
 * what is NOT allowed is a name with no connection to the merchant.
 */
export const STATEMENT_DESCRIPTOR = 'GIGGONE';
export const STATEMENT_DESCRIPTOR_SUFFIX = 'GIGGONE LEADS';
export const SUPPORT_EMAIL = 'support@giggone.com';
export const SUPPORT_PHONE_NOTE = 'Shown on the receipt so a cardholder calls you before the bank.';

export type BrandVerdict = 'rebrand' | 'rebrand_with_disclosure' | 'legal_entity_required';

export interface PaymentSurface {
  /** Where the customer sees it. */
  surface: string;
  /** Where it is changed. */
  where: string;
  verdict: BrandVerdict;
  /** Exactly what to set it to. */
  setTo: string;
  note: string;
}

/**
 * Every customer-facing payment surface, with the verdict for each.
 * Ordered by how visible it is to a paying contractor.
 */
export const PAYMENT_SURFACES: PaymentSurface[] = [
  {
    surface: 'Stripe public business name (shown on receipts, Checkout, portal)',
    where: 'Stripe Dashboard → Settings → Business → Public details → Public business name',
    verdict: 'rebrand',
    setTo: 'GIGGONE',
    note:
      'This single field is what printed "Sky Is The Limit Shop" on your $99 receipt. Set it to GIGGONE and keep the legal name in the Legal business name field below it — Stripe keeps the two separate on purpose.',
  },
  {
    surface: 'Stripe legal business name / tax details',
    where: 'Stripe Dashboard → Settings → Business → Legal business name, EIN, bank account',
    verdict: 'legal_entity_required',
    setTo: 'Sky Is The Limit Shop LLC',
    note:
      'Must match the EIN, the bank account and the LLC registration exactly. Never change this to GIGGONE — payouts and 1099-K reporting key off it.',
  },
  {
    surface: 'Card statement descriptor (the line on the bank statement)',
    where: 'Stripe Dashboard → Settings → Payments → Statement descriptor',
    verdict: 'rebrand',
    setTo: 'GIGGONE',
    note:
      'Max 22 chars. A trade name of the registered entity is permitted. Also set the shortened descriptor to GIGGONE and add a support phone — unrecognised descriptors are the #1 cause of chargebacks. Stripe currently lists this field as still required on the connected account.',
  },
  {
    surface: 'Receipt / invoice header and email sender name',
    where: 'Stripe → Settings → Customer emails + Branding (icon, logo, brand colour)',
    verdict: 'rebrand_with_disclosure',
    setTo: 'GIGGONE (logo + colour), footer line: "GIGGONE — a service of Sky Is The Limit Shop LLC"',
    note:
      'Receipts may carry the trade name as long as the entity is identifiable. Upload the GigGone mark and set the brand colour here.',
  },
  {
    surface: 'GoHighLevel checkout / hosted payment page',
    where: 'GHL → Settings → Business Profile (name, logo) and Payments → Settings',
    verdict: 'rebrand',
    setTo: 'GIGGONE',
    note:
      'The business name and logo in the GHL business profile drive the hosted checkout header, the order summary and the GHL order confirmation email. Rename it to GIGGONE.',
  },
  {
    surface: 'GoHighLevel product / payment link names',
    where: 'GHL → Payments → Products → each product name and description',
    verdict: 'rebrand',
    setTo: 'GIGGONE Basic / Pro / Premium Membership',
    note: 'The product name appears on the checkout page, the receipt line item and the customer’s bank descriptor suffix.',
  },
  {
    surface: 'Post-payment success page',
    where: 'This app — /payment-complete (return URL on each payment link)',
    verdict: 'rebrand',
    setTo: 'GIGGONE',
    note:
      'Already GigGone-branded. This is why the return URL matters: it replaces the generic CRM thank-you page with your own.',
  },
  {
    surface: 'Payment confirmation / dunning emails sent by GigGone',
    where: 'This app — send-transactional-email (From name and template footer)',
    verdict: 'rebrand_with_disclosure',
    setTo: 'From: GIGGONE <billing@giggone.com>, footer: GIGGONE — a service of Sky Is The Limit Shop LLC',
    note: 'CAN-SPAM requires a real postal address and a true sender identity; a trade name plus the entity in the footer satisfies both.',
  },
  {
    surface: 'Customer portal / subscription management',
    where: 'Stripe → Settings → Billing → Customer portal (or GHL customer portal branding)',
    verdict: 'rebrand_with_disclosure',
    setTo: 'GIGGONE header + "Operated by Sky Is The Limit Shop LLC"',
    note: 'Portal branding follows the public business name and the branding assets, so fixing those two fixes this automatically.',
  },
  {
    surface: 'Terms, Privacy, Refund policy, contracts',
    where: 'This app — /legal',
    verdict: 'legal_entity_required',
    setTo: '"GIGGONE, a service of Sky Is The Limit Shop LLC" — with the LLC named as the contracting party',
    note: 'The party that can be sued and that holds the merchant account must be named. Lead with the brand, contract as the LLC.',
  },
  {
    surface: 'Dispute / chargeback evidence submitted to the bank',
    where: 'Stripe → Disputes → evidence, and your response templates',
    verdict: 'legal_entity_required',
    setTo: 'Sky Is The Limit Shop LLC d/b/a GIGGONE',
    note: 'Use the d/b/a form so the issuer can tie the descriptor the cardholder saw to the registered merchant.',
  },
  {
    surface: 'Bank account, payouts, tax forms (1099-K)',
    where: 'Stripe → Settings → Bank accounts and Tax',
    verdict: 'legal_entity_required',
    setTo: 'Sky Is The Limit Shop LLC',
    note: 'Banking and tax identity cannot use a trade name. Customers never see these.',
  },
];

export const BRAND_ACTION_ORDER: string[] = [
  'Stripe → Settings → Business → Public details → Public business name = GIGGONE (fixes receipts, Checkout and the portal in one edit).',
  'Stripe → Settings → Payments → Statement descriptor = GIGGONE, shortened descriptor = GIGGONE, add support phone.',
  'Stripe → Settings → Branding → upload the GigGone logo/icon and set the brand colour.',
  'Stripe → Settings → Business → Legal business name stays Sky Is The Limit Shop LLC (do not touch the EIN or bank account).',
  'GoHighLevel → Settings → Business Profile → name = GIGGONE, upload the logo (fixes hosted checkout + CRM receipts).',
  'GoHighLevel → Payments → Products → rename to "GIGGONE Basic / Pro / Premium Membership".',
  'Legal pages and dispute templates keep the LLC as the named party, written as "Sky Is The Limit Shop LLC d/b/a GIGGONE".',
];
